import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Form, Pagination, Modal, Alert, OverlayTrigger, Tooltip, Badge, Card, Collapse, Spinner } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import { apiGet, apiPost, API_BASE_URL } from '../utils/apiConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';

const documentosPorPagina = 10;

// Paleta corporativa para firmantes
const COLORES_FIRMANTES = [
  { bg: '#e8eaf6', border: '#3949ab', text: '#283593' },
  { bg: '#e0f2f1', border: '#00897b', text: '#00695c' },
  { bg: '#fff3e0', border: '#f57c00', text: '#e65100' },
  { bg: '#f3e5f5', border: '#8e24aa', text: '#6a1b9a' },
  { bg: '#fce4ec', border: '#e53935', text: '#c62828' },
  { bg: '#e0f7fa', border: '#00838f', text: '#006064' },
];

const obtenerColor = (index) => COLORES_FIRMANTES[index % COLORES_FIRMANTES.length];

// Estilos del header de tabla (reemplaza el amarillo)
const HEADER_STYLE = {
  backgroundColor: '#2c3e50',
  color: '#ecf0f1',
  borderColor: '#34495e',
  fontSize: '0.82rem',
  fontWeight: '600',
  letterSpacing: '0.02em'
};

function AdministrativeDocumentsPage() {
  const [documentos, setDocumentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [sidebarAbierto, setSidebarAbierto] = useState(window.innerWidth >= 992);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
  const [sinPermisos, setSinPermisos] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const nombres = localStorage.getItem('nombres') || 'Usuario';

  // Estados desplegable + popup
  const [expandidos, setExpandidos] = useState({});
  const [mostrarModalFirmantes, setMostrarModalFirmantes] = useState(false);
  const [documentoFirmantes, setDocumentoFirmantes] = useState(null);

  // Estados para reenvío
  const [mostrarModalReenvio, setMostrarModalReenvio] = useState(false);
  const [documentoReenvio, setDocumentoReenvio] = useState(null);
  const [firmanteReenvioIndex, setFirmanteReenvioIndex] = useState(null);
  const [correoReenvio, setCorreoReenvio] = useState('');
  const [celularReenvio, setCelularReenvio] = useState('');
  const [metodosReenvio, setMetodosReenvio] = useState([]);
  const [enviandoReenvio, setEnviandoReenvio] = useState(false);
  const [mensajeReenvio, setMensajeReenvio] = useState('');

  // Estados para descarga Certicámara
  const [descargandoCerticamara, setDescargandoCerticamara] = useState({});

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      if (mobile) setSidebarAbierto(false);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const decodificarNombre = (nombre) => {
    if (!nombre) return '';
    try { return decodeURIComponent(nombre); } catch { return nombre; }
  };

  const totalPaginas = useMemo(() => Math.ceil(total / documentosPorPagina) || 1, [total]);

  // ═══════════════════════════════════════════════════════════════
  // BADGES
  // ═══════════════════════════════════════════════════════════════
  const estadoFirmanteBadge = (estado) => {
    const map = {
      'pendiente': { color: 'warning', icon: '⏳', texto: 'Pendiente' },
      'enviado': { color: 'info', icon: '📤', texto: 'Enviado' },
      'en_firma': { color: 'primary', icon: '✍️', texto: 'En firma' },
      'firmado': { color: 'success', icon: '✅', texto: 'Firmado' },
      'rechazado': { color: 'danger', icon: '❌', texto: 'Rechazado' },
      'cancelado': { color: 'dark', icon: '🚫', texto: 'Cancelado' }
    };
    const config = map[estado] || map['pendiente'];
    return (
      <Badge bg={config.color} className={config.color === 'warning' ? 'text-dark' : ''}>
        {config.icon} {config.texto}
      </Badge>
    );
  };

  const estadoCerticamaraBadge = (estado) => {
    const map = {
      'pendiente': { color: 'secondary', texto: 'Pendiente' },
      'enviado': { color: 'info', texto: 'Enviado' },
      'link_listo': { color: 'primary', texto: 'Link Listo' },
      'firmado': { color: 'success', texto: 'Firmado' },
      'rechazado': { color: 'danger', texto: 'Rechazado' },
      'cancelado': { color: 'dark', texto: 'Cancelado' },
      'error': { color: 'danger', texto: 'Error' },
      'esperando_otros': { color: 'secondary', texto: 'Esperando' },
    };
    const config = map[estado] || { color: 'secondary', texto: estado || '-' };
    return <Badge bg={config.color} style={{ fontSize: '0.65rem' }}>{config.texto}</Badge>;
  };

  const estadoBadge = (estado) => {
    const map = { 
      rechazado: 'danger', pendiente: 'warning', finalizado: 'success', 
      firmado: 'info', anulado: 'secondary', 'parcialmente_firmado': 'info'
    };
    const iconos = { 
      rechazado: '❌ ', pendiente: '⌛ ', finalizado: '✅ ', 
      firmado: '✍️ ', anulado: '🚫 ', 'parcialmente_firmado': '📝 '
    };
    return (
      <Badge bg={map[estado] || 'secondary'} className={estado === 'pendiente' ? 'text-dark' : ''}>
        {iconos[estado] || ''}{(estado || '').toUpperCase()}
      </Badge>
    );
  };

  const calcularProgresoFirmas = (firmantes) => {
    if (!firmantes || firmantes.length === 0) return null;
    const firmados = firmantes.filter(f => f.estado === 'firmado').length;
    const enFirma = firmantes.filter(f => f.estado === 'en_firma').length;
    const rechazados = firmantes.filter(f => f.estado === 'rechazado').length;
    const cancelados = firmantes.filter(f => f.estado === 'cancelado').length;
    const total = firmantes.length;
    const completados = firmados + enFirma;
    return { firmados, enFirma, rechazados, cancelados, total, completados, porcentaje: Math.round((firmados / total) * 100) };
  };

  const copyToClipboard = (text) => navigator.clipboard?.writeText(text).catch(() => {});

  const renderMetodoEnvio = (metodos) => {
    let metodosArray = [];
    if (Array.isArray(metodos)) {
      if (metodos.length > 0 && typeof metodos[0] === 'string' && metodos[0].startsWith('[')) {
        try { metodosArray = JSON.parse(metodos[0]); } catch { metodosArray = metodos; }
      } else { metodosArray = metodos; }
    } else if (typeof metodos === 'string') {
      try { metodosArray = JSON.parse(metodos); } catch { metodosArray = [metodos]; }
    }
    if (metodosArray.length === 0) return <span className="text-muted small">N/A</span>;
    return (
      <div className="d-flex gap-2 flex-wrap justify-content-center">
        {metodosArray.map((m, i) => {
          const map = { 
            correo: ['bi-envelope-fill', 'Correo', 'primary'], 
            sms: ['bi-chat-dots-fill', 'SMS', 'success'], 
            whatsapp: ['bi-whatsapp', 'WhatsApp', 'success'] 
          };
          const conf = map[m];
          return conf ? (
            <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{conf[1]}</Tooltip>}>
              <i className={`bi ${conf[0]} text-${conf[2]}`} style={{ fontSize: '1rem' }} />
            </OverlayTrigger>
          ) : null;
        })}
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // EXPANDIR / CONTRAER
  // ═══════════════════════════════════════════════════════════════
  const toggleExpandir = (docId) => {
    setExpandidos(prev => ({ ...prev, [docId]: !prev[docId] }));
  };

  const contraerTodo = () => setExpandidos({});

  const hayAlgunoExpandido = Object.values(expandidos).some(v => v);

  // ═══════════════════════════════════════════════════════════════
  // POPUP FIRMANTES
  // ═══════════════════════════════════════════════════════════════
  const handleVerFirmantes = (doc) => {
    setDocumentoFirmantes(doc);
    setMostrarModalFirmantes(true);
  };

  // ═══════════════════════════════════════════════════════════════
  // VER / DESCARGAR DESDE CERTICÁMARA
  // ⭐ FIX V5.1: Quitado && doc.certicamaraProcessId (nunca se llena)
  // ═══════════════════════════════════════════════════════════════
  const handleVerDocumentoCerticamara = async (doc) => {
    // ⭐ FIX: Solo verificar estadoCerticamara, no certicamaraProcessId
    if (doc.estadoCerticamara === 'firmado') {
      setDescargandoCerticamara(prev => ({ ...prev, [doc._id]: 'ver' }));
      try {
        const response = await apiGet(`/api/certicamara/documento-firmado/${doc._id}`);
        if (response.url) {
          // ⭐ FIX V5.2: Descargar el PDF de S3 como blob para mostrarlo en iframe
          // Las URLs pre-signed de S3 no se renderizan bien en iframe directamente
          try {
            const pdfResponse = await fetch(response.url);
            const pdfBlob = await pdfResponse.blob();
            const blobUrl = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
            setDocumentoSeleccionado(blobUrl);
            setMostrarModal(true);
          } catch (fetchError) {
            console.error('Error descargando PDF de S3, intentando URL directa:', fetchError);
            // Fallback: usar URL directa (puede no renderizar pero al menos abre)
            setDocumentoSeleccionado(response.url);
            setMostrarModal(true);
          }
        } else if (doc.rutaFinal?.trim()) {
          setDocumentoSeleccionado(`${API_BASE_URL}/${doc.rutaFinal}`);
          setMostrarModal(true);
        }
      } catch (error) {
        console.error('Error obteniendo PDF de Certicámara:', error);
        if (doc.rutaFinal?.trim()) {
          setDocumentoSeleccionado(`${API_BASE_URL}/${doc.rutaFinal}`);
          setMostrarModal(true);
        }
      } finally {
        setDescargandoCerticamara(prev => ({ ...prev, [doc._id]: null }));
      }
    } else if (doc.rutaFinal?.trim()) {
      setDocumentoSeleccionado(`${API_BASE_URL}/${doc.rutaFinal}`);
      setMostrarModal(true);
    }
  };

  const handleDescargarDocumentoCerticamara = async (doc) => {
    // ⭐ FIX: Solo verificar estadoCerticamara, no certicamaraProcessId
    if (doc.estadoCerticamara === 'firmado') {
      setDescargandoCerticamara(prev => ({ ...prev, [doc._id]: 'descargar' }));
      try {
        const response = await apiGet(`/api/certicamara/documento-firmado/${doc._id}`);
        if (response.url) {
          // Abrir URL de S3 directamente (descarga)
          const a = document.createElement('a');
          a.href = response.url;
          a.download = `${doc.nombreDocumento || 'documento'}_firmado.pdf`;
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else if (doc.rutaFinal?.trim()) {
          window.open(`${API_BASE_URL}/${doc.rutaFinal}`, '_blank');
        }
      } catch (error) {
        console.error('Error descargando de Certicámara:', error);
        if (doc.rutaFinal?.trim()) {
          window.open(`${API_BASE_URL}/${doc.rutaFinal}`, '_blank');
        }
      } finally {
        setDescargandoCerticamara(prev => ({ ...prev, [doc._id]: null }));
      }
    } else if (doc.rutaFinal?.trim()) {
      window.open(`${API_BASE_URL}/${doc.rutaFinal}`, '_blank');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // ⭐ V5.3b: Descargar certificado BIOFIRMAS (consolidado a nivel documento)
  // ═══════════════════════════════════════════════════════════════
  const handleDescargarCertificadoBiofirmas = async (doc) => {
    if (!doc.rutaCertificadoBiofirmas) {
      alert('No hay certificado BIOFIRMAS disponible para este documento.');
      return;
    }

    setDescargandoCerticamara(prev => ({ ...prev, [`biofirma-${doc._id}`]: 'biofirma' }));
    
    try {
      const url = `${API_BASE_URL}/${doc.rutaCertificadoBiofirmas}`;
      const pdfResponse = await fetch(url);
      
      if (pdfResponse.ok) {
        const pdfBlob = await pdfResponse.blob();
        const blobUrl = URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
        setDocumentoSeleccionado(blobUrl);
        setMostrarModal(true);
      } else {
        console.error(`Error descargando cert BIOFIRMAS: ${pdfResponse.status}`);
        alert('No se pudo descargar el certificado BIOFIRMAS.');
      }
    } catch (error) {
      console.error('Error descargando certificado BIOFIRMAS:', error);
      alert('Error al descargar el certificado BIOFIRMAS.');
    } finally {
      setDescargandoCerticamara(prev => ({ ...prev, [`biofirma-${doc._id}`]: null }));
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // ⭐ Certificado de CERTICÁMARA (firma digital)
  // Viene como { content: "base64...", fileName: "cert.pdf" }
  // ═══════════════════════════════════════════════════════════════
  const handleDescargarCertificado = async (doc) => {
    setDescargandoCerticamara(prev => ({ ...prev, [`cert-${doc._id}`]: 'certificado' }));
    try {
      const response = await apiGet(`/api/certicamara/certificado/${doc._id}`);
      if (response.certificado) {
        const cert = response.certificado;
        
        // Caso 1: Viene como URL directa
        if (cert.url) {
          window.open(cert.url, '_blank');
          return;
        }
        
        // Caso 2: Viene como { content: "JVBERi0x...", fileName: "cert.pdf" }
        // El content es un PDF en base64 — convertir a Blob y mostrar/descargar
        const base64Content = cert.content || (typeof cert === 'string' ? cert : null);
        const fileName = cert.fileName || `certificado_${doc.nombreDocumento || doc._id}.pdf`;
        
        if (base64Content && typeof base64Content === 'string' && base64Content.length > 100) {
          try {
            // Decodificar base64 a bytes
            const byteCharacters = atob(base64Content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            
            // Mostrar en el mismo modal que usa el ojito
            setDocumentoSeleccionado(blobUrl);
            setMostrarModal(true);
          } catch (decodeError) {
            console.error('Error decodificando certificado base64:', decodeError);
            // Fallback: forzar descarga directa
            try {
              const link = document.createElement('a');
              link.href = `data:application/pdf;base64,${base64Content}`;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } catch (dlError) {
              alert('Error al descargar el certificado.');
            }
          }
        } else {
          console.error('Formato de certificado no reconocido:', typeof cert);
          alert('El certificado no tiene un formato reconocido.');
        }
      } else {
        alert('No se recibió certificado de Certicámara.');
      }
    } catch (error) {
      console.error('Error obteniendo certificado:', error);
      alert('No se pudo obtener el certificado de firma. Intente más tarde.');
    } finally {
      setDescargandoCerticamara(prev => ({ ...prev, [`cert-${doc._id}`]: null }));
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // PERMISOS Y DATOS
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const permisos = localStorage.getItem('permisos');
    if (permisos) { 
      const p = JSON.parse(permisos); 
      if (!p.verDocumentosAdministrativos) setSinPermisos(true); 
    } else {
      setSinPermisos(true);
    }
  }, []);

  const obtenerDocumentos = async () => {
    try {
      const params = { page: pagina, limit: documentosPorPagina, cedula: busqueda, tipo: 'administrativo' };
      const datos = await apiGet('/api/documentos-dashboard', { params });
      setDocumentos(Array.isArray(datos.documentos) ? datos.documentos : []);
      setTotal(Number(datos.total || 0));
    } catch (err) { console.error('Error:', err); }
  };

  useEffect(() => {
    if (!sinPermisos) { 
      obtenerDocumentos(); 
      const id = setInterval(obtenerDocumentos, 7000); 
      return () => clearInterval(id); 
    }
  }, [pagina, busqueda, sinPermisos]);

  const handleBuscar = (e) => { e.preventDefault(); setPagina(1); obtenerDocumentos(); };
  const handleCerrarModal = () => { setMostrarModal(false); setDocumentoSeleccionado(null); };

  // ═══════════════════════════════════════════════════════════════
  // REENVÍO
  // ═══════════════════════════════════════════════════════════════
  const handleAbrirReenvio = (doc, firmanteIndex = null) => {
    setDocumentoReenvio(doc);
    setFirmanteReenvioIndex(firmanteIndex);
    if (firmanteIndex !== null && doc.firmantes && doc.firmantes[firmanteIndex]) {
      const firmante = doc.firmantes[firmanteIndex];
      setCorreoReenvio(firmante.correo || '');
      setCelularReenvio(firmante.celular || '');
      setMetodosReenvio(firmante.metodoEnvio || ['correo']);
    } else {
      setCorreoReenvio(doc.correo || '');
      setCelularReenvio(doc.celular || '');
      setMetodosReenvio(doc.sendMethods || ['correo']);
    }
    setMensajeReenvio('');
    setMostrarModalReenvio(true);
    setMostrarModalFirmantes(false);
  };

  const handleToggleMetodo = (metodo) => {
    setMetodosReenvio(prev => prev.includes(metodo) ? prev.filter(m => m !== metodo) : [...prev, metodo]);
  };

  const handleReenviarDocumento = async () => {
    if (!correoReenvio && !celularReenvio) { setMensajeReenvio('Debes ingresar al menos un correo o celular'); return; }
    if (correoReenvio && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoReenvio)) { setMensajeReenvio('El correo no es válido'); return; }
    if (celularReenvio && !/^\d{10}$/.test(celularReenvio)) { setMensajeReenvio('El celular debe tener 10 dígitos'); return; }
    if (metodosReenvio.length === 0) { setMensajeReenvio('Selecciona al menos un método'); return; }
    
    setEnviandoReenvio(true); setMensajeReenvio('Reenviando...');
    try {
      const payload = { correo: correoReenvio, celular: celularReenvio, sendMethods: metodosReenvio };
      if (firmanteReenvioIndex !== null) payload.firmanteIndex = firmanteReenvioIndex;
      await apiPost(`/api/reenviar-documento/${documentoReenvio._id}`, payload);
      setMensajeReenvio('✅ Reenviado exitosamente');
      setTimeout(() => { setMostrarModalReenvio(false); obtenerDocumentos(); }, 2000);
    } catch (error) { 
      setMensajeReenvio(`❌ Error: ${error.response?.data?.message || 'No se pudo reenviar'}`); 
    } finally { setEnviandoReenvio(false); }
  };


  // ═══════════════════════════════════════════════════════════════
  // PAGINACIÓN
  // ═══════════════════════════════════════════════════════════════
  const renderPaginacion = () => {
    if (totalPaginas <= 1) return null;
    const items = [];
    items.push(<Pagination.Prev key="prev" disabled={pagina === 1} onClick={() => setPagina(p => p - 1)} />);
    const start = Math.max(1, pagina - 2);
    const end = Math.min(totalPaginas, start + 4);
    const adjustedStart = Math.max(1, end - 4);
    if (adjustedStart > 1) { 
      items.push(<Pagination.Item key={1} onClick={() => setPagina(1)}>1</Pagination.Item>); 
      if (adjustedStart > 2) items.push(<Pagination.Ellipsis key="e1" disabled />); 
    }
    for (let i = adjustedStart; i <= end; i++) {
      items.push(<Pagination.Item key={i} active={i === pagina} onClick={() => setPagina(i)}>{i}</Pagination.Item>);
    }
    if (end < totalPaginas) { 
      if (end < totalPaginas - 1) items.push(<Pagination.Ellipsis key="e2" disabled />); 
      items.push(<Pagination.Item key={totalPaginas} onClick={() => setPagina(totalPaginas)}>{totalPaginas}</Pagination.Item>); 
    }
    items.push(<Pagination.Next key="next" disabled={pagina === totalPaginas} onClick={() => setPagina(p => p + 1)} />);
    return <Pagination className="justify-content-center mb-0" size={isMobile ? 'sm' : 'md'}>{items}</Pagination>;
  };

  // ═══════════════════════════════════════════════════════════════
  // BOTONES DE ACCIÓN PARA DOCUMENTOS FINALIZADOS
  // ═══════════════════════════════════════════════════════════════
  const AccionesDocumento = ({ doc }) => {
    const esCerticamaraFirmado = doc.estadoCerticamara === 'firmado';
    const tieneRutaLocal = doc.rutaFinal?.trim();
    const estaDescargando = descargandoCerticamara[doc._id];
    const estaCertificando = descargandoCerticamara[`cert-${doc._id}`];

    if (doc.estado === 'finalizado' || doc.estado === 'firmado') {
      // Para 'firmado': todos firmaron localmente, puede que Certicámara aún no confirme
      // Para 'finalizado': Certicámara ya confirmó o es flujo local completo
      const tieneArchivo = tieneRutaLocal || esCerticamaraFirmado;
      
      if (!tieneArchivo) {
        // Firmado pero aún sin archivo disponible (esperando Certicámara)
        return (
          <OverlayTrigger overlay={<Tooltip>Esperando firma digital de Certicámara</Tooltip>}>
            <span style={{ fontSize: '1rem' }}>
              <Spinner size="sm" variant="primary" className="me-1" />
              <small className="text-muted">Procesando...</small>
            </span>
          </OverlayTrigger>
        );
      }

      return (
        <div className="d-flex justify-content-center gap-1 flex-wrap">
          {/* ⭐ V5.3 Botón 1: OJITO — Ver documento firmado de Certicámara */}
          <OverlayTrigger overlay={<Tooltip>Ver documento firmado (Certicámara)</Tooltip>}>
            <Button 
              variant="primary"
              size="sm" 
              onClick={() => handleVerDocumentoCerticamara(doc)}
              disabled={!!estaDescargando}
            >
              {estaDescargando === 'ver' ? <Spinner size="sm" /> : <i className="bi bi-eye-fill"></i>}
            </Button>
          </OverlayTrigger>

          {/* ⭐ V5.3 Botón 2: CERTIFICADO — Certificado de Certicámara */}
          {esCerticamaraFirmado && (
            <OverlayTrigger overlay={<Tooltip>Certificado de firma digital (Certicámara)</Tooltip>}>
              <Button 
                variant="outline-info" 
                size="sm" 
                onClick={() => handleDescargarCertificado(doc)}
                disabled={!!estaCertificando}
              >
                {estaCertificando ? <Spinner size="sm" /> : <i className="bi bi-award"></i>}
              </Button>
            </OverlayTrigger>
          )}

          {/* ⭐ V5.3b Botón 3: DESCARGA — Certificado BIOFIRMAS (selfie+firma+GPS) */}
          {doc.rutaCertificadoBiofirmas && (
            <OverlayTrigger overlay={<Tooltip>Descargar certificado BIOFIRMAS</Tooltip>}>
              <Button 
                variant="success" 
                size="sm" 
                onClick={() => handleDescargarCertificadoBiofirmas(doc)}
                disabled={!!descargandoCerticamara[`biofirma-${doc._id}`]}
              >
                {descargandoCerticamara[`biofirma-${doc._id}`] ? <Spinner size="sm" /> : <i className="bi bi-download"></i>}
              </Button>
            </OverlayTrigger>
          )}
        </div>
      );
    }

    if (doc.estado === 'rechazado' && tieneRutaLocal) {
      return (
        <Button variant="outline-danger" size="sm" onClick={() => { setDocumentoSeleccionado(`${API_BASE_URL}/${doc.rutaFinal}`); setMostrarModal(true); }}>
          <i className="bi bi-eye-fill"></i>
        </Button>
      );
    }

    if (doc.estado === 'pendiente') {
      const tieneFirmantes = doc.firmantes && doc.firmantes.length > 0;
      if (!tieneFirmantes) {
        return (
          <Button variant="outline-primary" size="sm" onClick={() => handleAbrirReenvio(doc)} title="Reenviar">
            <i className="bi bi-arrow-repeat"></i>
          </Button>
        );
      }
      return <span className="text-muted" style={{ fontSize: '1rem' }}>⏳</span>;
    }

    if (doc.estado === 'anulado') return <span className="text-muted">🚫</span>;

    return null;
  };

  // ═══════════════════════════════════════════════════════════════
  // FILA EXPANDIDA DE FIRMANTES
  // ═══════════════════════════════════════════════════════════════
  const FilaFirmantes = ({ doc }) => {
    if (!doc.firmantes || doc.firmantes.length === 0) return null;
    const progreso = calcularProgresoFirmas(doc.firmantes);
    
    return (
      <tr>
        <td colSpan="8" className="p-0">
          <Collapse in={expandidos[doc._id]}>
            <div>
              <div className="p-3" style={{ backgroundColor: '#f5f6fa', borderTop: '2px solid #dfe6e9' }}>
                {progreso && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <small className="fw-bold text-secondary">Progreso de firmas</small>
                      <small className="text-muted">
                        {progreso.rechazados > 0 
                          ? `❌ Rechazado (${progreso.firmados} firmaron, ${progreso.rechazados} rechazó, ${progreso.cancelados} cancelado${progreso.cancelados !== 1 ? 's' : ''})`
                          : `${progreso.firmados} de ${progreso.total}${progreso.enFirma > 0 ? ` (${progreso.enFirma} en proceso)` : ''}`
                        }
                      </small>
                    </div>
                    <div className="progress" style={{ height: '6px', backgroundColor: '#dfe6e9' }}>
                      {progreso.rechazados > 0 ? (
                        <>
                          <div className="progress-bar bg-success" style={{ width: `${(progreso.firmados / progreso.total) * 100}%` }} />
                          <div className="progress-bar bg-danger" style={{ width: `${(progreso.rechazados / progreso.total) * 100}%` }} />
                          <div className="progress-bar bg-secondary" style={{ width: `${(progreso.cancelados / progreso.total) * 100}%`, opacity: 0.5 }} />
                        </>
                      ) : (
                        <>
                          <div 
                            className={`progress-bar ${progreso.porcentaje === 100 ? 'bg-success' : ''}`}
                            style={{ width: `${progreso.porcentaje}%`, backgroundColor: progreso.porcentaje === 100 ? undefined : '#3949ab' }}
                          />
                          {progreso.enFirma > 0 && (
                            <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${(progreso.enFirma / progreso.total) * 100}%`, backgroundColor: '#64b5f6' }} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="table-responsive">
                  <table className="table table-sm mb-0" style={{ fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#dfe6e9' }}>
                        <th style={{ width: '36px' }}>#</th>
                        <th>Nombre</th>
                        <th>Cédula</th>
                        <th>Correo</th>
                        <th>Celular</th>
                        <th className="text-center">Método</th>
                        <th className="text-center">Estado</th>
                        <th className="text-center">Certicámara</th>
                        <th className="text-center" style={{ width: '80px' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.firmantes.map((firmante, index) => {
                        const color = obtenerColor(index);
                        return (
                          <tr 
                            key={index}
                            style={{ 
                              borderLeft: `4px solid ${color.border}`,
                              backgroundColor: firmante.estado === 'firmado' ? '#e8f5e9' : 
                                              firmante.estado === 'en_firma' ? '#e3f2fd' :
                                              firmante.estado === 'rechazado' ? '#ffebee' : 
                                              firmante.estado === 'cancelado' ? '#f5f5f5' : 'white',
                            opacity: firmante.estado === 'cancelado' ? 0.7 : 1
                            }}
                          >
                            <td className="text-center">
                              <span 
                                className="badge rounded-circle d-inline-flex align-items-center justify-content-center"
                                style={{ width: '22px', height: '22px', backgroundColor: color.border, color: 'white', fontSize: '0.7rem' }}
                              >
                                {index + 1}
                              </span>
                            </td>
                            <td className="fw-bold" style={{ color: color.text }}>{firmante.nombre || '-'}</td>
                            <td>
                              {firmante.cedula || '-'}
                              <Button variant="link" size="sm" className="p-0 ms-1" onClick={() => copyToClipboard(firmante.cedula)}>
                                <i className="bi bi-clipboard small"></i>
                              </Button>
                            </td>
                            <td style={{ wordBreak: 'break-all', fontSize: '0.78rem' }}>{firmante.correo || '-'}</td>
                            <td>{firmante.celular || '-'}</td>
                            <td className="text-center">{renderMetodoEnvio(firmante.metodoEnvio)}</td>
                            <td className="text-center">{estadoFirmanteBadge(firmante.estado || 'pendiente')}</td>
                            <td className="text-center">{estadoCerticamaraBadge(firmante.estadoCerticamaraFirmante)}</td>
                            <td className="text-center">
                              {firmante.estado === 'firmado' ? (
                                <i className="bi bi-check-circle-fill text-success"></i>
                              ) : firmante.estado === 'en_firma' ? (
                                <OverlayTrigger overlay={<Tooltip>Completó BIOFIRMAS, esperando firma digital</Tooltip>}>
                                  <span><Spinner size="sm" variant="primary" /></span>
                                </OverlayTrigger>
                              ) : firmante.estado === 'rechazado' ? (
                                <OverlayTrigger overlay={<Tooltip>Rechazó el documento en Certicámara</Tooltip>}>
                                  <i className="bi bi-x-circle-fill text-danger"></i>
                                </OverlayTrigger>
                              ) : firmante.estado === 'cancelado' ? (
                                <OverlayTrigger overlay={<Tooltip>{firmante.motivoCancelacion || 'Cancelado por rechazo de otro firmante'}</Tooltip>}>
                                  <i className="bi bi-slash-circle-fill text-secondary"></i>
                                </OverlayTrigger>
                              ) : (
                                <Button variant="outline-primary" size="sm" onClick={() => handleAbrirReenvio(doc, index)} title="Reenviar">
                                  <i className="bi bi-arrow-repeat"></i>
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Collapse>
        </td>
      </tr>
    );
  };

  // ═══════════════════════════════════════════════════════════════
  // CARD MÓVIL
  // ═══════════════════════════════════════════════════════════════
  const RowCard = ({ doc }) => {
    const tieneFirmantes = doc.firmantes && doc.firmantes.length > 0;
    const progreso = calcularProgresoFirmas(doc.firmantes);
    
    return (
      <Card className="mb-3 shadow-sm border-0" style={{ borderRadius: '10px' }}>
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <h6 className="mb-1" style={{ fontSize: '0.95rem', color: '#2c3e50' }}>{doc.nombreDocumento}</h6>
              <small className="text-muted">📅 {new Date(doc.createdAt).toLocaleDateString('es-CO')}</small>
            </div>
            {estadoBadge(doc.estado)}
          </div>
          
          {progreso && (
            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Firmas: {progreso.firmados}/{progreso.total}</small>
                <small className="text-muted">{progreso.porcentaje}%</small>
              </div>
              <div className="progress" style={{ height: '5px', backgroundColor: '#ecf0f1' }}>
                <div className="progress-bar" style={{ width: `${progreso.porcentaje}%`, backgroundColor: '#3949ab' }} />
              </div>
            </div>
          )}
          
          <div className="mb-2 small"><strong>Enviado por:</strong> {decodificarNombre(doc.enviadoPor) || 'N/A'}</div>
          
          {/* Botones desplegable y popup para firmantes */}
          {tieneFirmantes && (
            <div className="d-flex gap-2 mb-2">
              <Button variant="outline-secondary" size="sm" onClick={() => toggleExpandir(doc._id)} className="flex-grow-1">
                <i className={`bi bi-chevron-${expandidos[doc._id] ? 'up' : 'down'} me-1`}></i>
                {expandidos[doc._id] ? 'Ocultar' : 'Desplegar'}
              </Button>
              <Button variant="outline-primary" size="sm" onClick={() => handleVerFirmantes(doc)}>
                <i className="bi bi-people-fill me-1"></i>{doc.firmantes.length}
              </Button>
            </div>
          )}
          
          {/* Firmantes expandibles */}
          <Collapse in={expandidos[doc._id]}>
            <div>
              {doc.firmantes?.map((firmante, index) => {
                const color = obtenerColor(index);
                return (
                  <div key={index} className="p-2 mb-2 rounded" style={{ borderLeft: `4px solid ${color.border}`, backgroundColor: color.bg }}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-bold" style={{ color: color.text, fontSize: '0.88rem' }}>
                        {index + 1}. {firmante.nombre}
                      </span>
                      {estadoFirmanteBadge(firmante.estado || 'pendiente')}
                    </div>
                    <div className="small text-muted">CC: {firmante.cedula} | {firmante.correo || firmante.celular || 'Sin contacto'}</div>
                    {firmante.estadoCerticamaraFirmante && (
                      <div className="mt-1">{estadoCerticamaraBadge(firmante.estadoCerticamaraFirmante)}</div>
                    )}
                    {firmante.estado === 'en_firma' && (
                      <div className="mt-1"><Spinner size="sm" variant="primary" className="me-1" /><small className="text-primary">En proceso de firma digital...</small></div>
                    )}
                    {firmante.estado === 'cancelado' && (
                      <div className="mt-1"><small className="text-secondary"><i className="bi bi-slash-circle me-1"></i>{firmante.motivoCancelacion || 'Cancelado'}</small></div>
                    )}
                    {firmante.estado !== 'firmado' && firmante.estado !== 'rechazado' && firmante.estado !== 'en_firma' && firmante.estado !== 'cancelado' && (
                      <Button variant="outline-primary" size="sm" className="mt-1" onClick={() => handleAbrirReenvio(doc, index)}>
                        <i className="bi bi-arrow-repeat me-1"></i>Reenviar
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </Collapse>
          
          {/* Acciones del documento */}
          <div className="mt-2"><AccionesDocumento doc={doc} /></div>
        </Card.Body>
      </Card>
    );
  };


  // ═══════════════════════════════════════════════════════════════
  // LAYOUT
  // ═══════════════════════════════════════════════════════════════
  const getMainContentStyle = () => isMobile 
    ? { marginLeft: 0, width: '100%', padding: '70px 15px 20px 15px', transition: 'all 0.3s ease' } 
    : { marginLeft: sidebarAbierto ? '250px' : '70px', width: sidebarAbierto ? 'calc(100% - 250px)' : 'calc(100% - 70px)', padding: '20px 30px', transition: 'all 0.3s ease' };

  if (sinPermisos) return (
    <div className="d-flex" style={{ minHeight: '100vh' }}>
      <Sidebar onToggle={setSidebarAbierto} />
      <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={getMainContentStyle()}>
        <Alert variant="warning" className="text-center">
          <h4>🚫 Acceso Denegado</h4>
          <p className="mb-0">No tienes permisos para ver documentos administrativos.</p>
        </Alert>
      </div>
    </div>
  );

  return (
    <div className="d-flex" style={{ minHeight: '100vh', backgroundColor: '#f5f6fa' }}>
      <Sidebar onToggle={setSidebarAbierto} />
      <div className="flex-grow-1" style={getMainContentStyle()}>
        {/* Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4">
          <div className="mb-2 mb-md-0">
            <h2 className="mb-1" style={{ fontSize: isMobile ? '1.3rem' : '1.75rem', color: '#2c3e50' }}>
              📋 Documentos Administrativos
            </h2>
            <p className="text-muted mb-0 small">Contratos y documentos multi-firmante</p>
          </div>
          <div className="text-start text-md-end">
            <div className="fw-bold" style={{ fontSize: '1rem', color: '#2c3e50' }}>
              👋 {decodificarNombre(nombres)}
            </div>
            <small className="text-muted text-capitalize">
              {(localStorage.getItem('rol') || 'usuario').replace('admin', ' Admin')}
            </small>
          </div>
        </div>

        {/* Buscador */}
        <Card className="shadow-sm mb-3 border-0" style={{ borderRadius: '10px' }}>
          <Card.Body className="p-3">
            <Form onSubmit={handleBuscar}>
              <div className="row align-items-end g-2">
                <div className="col-12 col-md-8 mb-2 mb-md-0">
                  <Form.Label className="mb-1 small fw-bold text-secondary">
                    <i className="bi bi-search me-1"></i>Buscar por Cédula
                  </Form.Label>
                  <Form.Control type="text" placeholder="Ingresa la cédula de cualquier firmante" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} size="sm" />
                </div>
                <div className="col-12 col-md-4">
                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" className="flex-grow-1" size="sm" style={{ backgroundColor: '#3949ab', borderColor: '#3949ab' }}>
                      <i className="bi bi-search me-1"></i>Buscar
                    </Button>
                    {busqueda && (
                      <Button variant="outline-secondary" onClick={() => { setBusqueda(''); setPagina(1); }} size="sm">
                        <i className="bi bi-x-circle"></i>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Form>
            <div className="mt-2 text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              Mostrando {documentos.length} de {total}
              {busqueda && ` (filtrado: ${busqueda})`}
            </div>
          </Card.Body>
        </Card>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* TABLA DESKTOP */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div className="d-none d-lg-block">
          <div className="table-responsive shadow-sm" style={{ maxHeight: '65vh', overflow: 'auto', borderRadius: '10px' }}>
            <Table bordered hover size="sm" className="mb-0">
              <thead style={{ ...HEADER_STYLE, position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  {/* Columna contraer/expandir */}
                  <th style={{ ...HEADER_STYLE, width: '44px', textAlign: 'center' }}>
                    {hayAlgunoExpandido ? (
                      <OverlayTrigger overlay={<Tooltip>Contraer todo</Tooltip>}>
                        <Button variant="link" size="sm" className="p-0 text-light" onClick={contraerTodo}>
                          <i className="bi bi-arrows-collapse"></i>
                        </Button>
                      </OverlayTrigger>
                    ) : (
                      <i className="bi bi-list" style={{ opacity: 0.5 }}></i>
                    )}
                  </th>
                  <th style={{ ...HEADER_STYLE, width: '90px' }}>Fecha</th>
                  <th style={{ ...HEADER_STYLE, minWidth: '180px' }}>Documento</th>
                  <th style={{ ...HEADER_STYLE, width: '120px', textAlign: 'center' }}>Firmantes</th>
                  <th style={{ ...HEADER_STYLE, width: '80px', textAlign: 'center' }}>Orden</th>
                  <th style={{ ...HEADER_STYLE, width: '110px' }}>Estado</th>
                  <th style={{ ...HEADER_STYLE, minWidth: '130px' }}>Enviado por</th>
                  <th style={{ ...HEADER_STYLE, width: '130px', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {documentos.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <i className="bi bi-clipboard-x" style={{ fontSize: '3rem', color: '#bdc3c7' }}></i>
                      <p className="text-muted mt-3 mb-1">No hay documentos administrativos</p>
                      {busqueda && (
                        <Button variant="outline-secondary" size="sm" onClick={() => { setBusqueda(''); setPagina(1); }}>
                          Limpiar búsqueda
                        </Button>
                      )}
                    </td>
                  </tr>
                ) : (
                  documentos.map((doc) => {
                    const tieneFirmantes = doc.firmantes && doc.firmantes.length > 0;
                    const progreso = calcularProgresoFirmas(doc.firmantes);
                    
                    return (
                      <React.Fragment key={doc._id}>
                        <tr className={doc.estado === 'rechazado' ? 'table-danger' : ''} style={{ transition: 'background-color 0.2s' }}>
                          {/* Chevron expandir */}
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            {tieneFirmantes && (
                              <Button variant="link" size="sm" className="p-0 text-secondary" onClick={() => toggleExpandir(doc._id)}>
                                <i className={`bi bi-chevron-${expandidos[doc._id] ? 'up' : 'down'}`} style={{ fontSize: '0.9rem' }}></i>
                              </Button>
                            )}
                          </td>
                          
                          {/* Fecha */}
                          <td className="small" style={{ verticalAlign: 'middle' }}>
                            {new Date(doc.createdAt).toLocaleDateString('es-CO')}
                            <br />
                            <small className="text-muted">
                              {new Date(doc.createdAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                            </small>
                          </td>
                          
                          {/* Documento */}
                          <td style={{ verticalAlign: 'middle' }}>
                            <div style={{ maxWidth: '280px', wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: '1.3' }}>
                              <strong style={{ color: '#2c3e50' }}>{doc.nombreDocumento || '-'}</strong>
                            </div>
                          </td>
                          
                          {/* Firmantes: chevron + botón popup */}
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            {tieneFirmantes ? (
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => handleVerFirmantes(doc)}
                                  style={{ fontSize: '0.78rem', borderColor: '#3949ab', color: '#3949ab' }}
                                >
                                  <i className="bi bi-people-fill me-1"></i>{doc.firmantes.length}
                                </Button>
                                {progreso && (
                                  <small className={progreso.firmados === progreso.total ? 'text-success fw-bold' : 'text-muted'} style={{ fontSize: '0.75rem' }}>
                                    {progreso.firmados}/{progreso.total}
                                  </small>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">1</span>
                            )}
                          </td>
                          
                          {/* Orden */}
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            {doc.ordenFirma === 'paralelo' ? (
                              <OverlayTrigger overlay={<Tooltip>Todos firman al tiempo</Tooltip>}>
                                <Badge style={{ backgroundColor: '#3949ab', fontSize: '0.7rem' }}>⚡ Paralelo</Badge>
                              </OverlayTrigger>
                            ) : (
                              <OverlayTrigger overlay={<Tooltip>Firman en orden</Tooltip>}>
                                <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>📝 Secuencial</Badge>
                              </OverlayTrigger>
                            )}
                          </td>
                          
                          {/* Estado */}
                          <td style={{ verticalAlign: 'middle' }}>{estadoBadge(doc.estado)}</td>
                          
                          {/* Enviado por */}
                          <td className="small" style={{ verticalAlign: 'middle' }}>
                            {decodificarNombre(doc.enviadoPor) || <span className="text-muted fst-italic">N/A</span>}
                          </td>
                          
                          {/* Acciones */}
                          <td className="text-center" style={{ verticalAlign: 'middle' }}>
                            <AccionesDocumento doc={doc} />
                          </td>
                        </tr>
                        
                        {/* Fila expandible */}
                        {tieneFirmantes && <FilaFirmantes doc={doc} />}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Vista móvil */}
        <div className="d-block d-lg-none">
          {documentos.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-clipboard-x" style={{ fontSize: '3rem', color: '#bdc3c7' }} />
              <p className="text-muted mt-3">No hay documentos</p>
            </div>
          ) : (
            documentos.map((doc) => <RowCard key={doc._id} doc={doc} />)
          )}
        </div>

        {/* Paginación */}
        <div className="mt-3">{renderPaginacion()}</div>


        {/* ═══════════════════════════════════════════════════════ */}
        {/* MODAL POPUP FIRMANTES */}
        {/* ═══════════════════════════════════════════════════════ */}
        <Modal show={mostrarModalFirmantes} onHide={() => setMostrarModalFirmantes(false)} centered size="lg">
          <Modal.Header closeButton style={{ backgroundColor: '#2c3e50', color: 'white' }}>
            <Modal.Title><i className="bi bi-people-fill me-2"></i>Firmantes del Documento</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            {documentoFirmantes && (
              <div>
                {/* Info del documento */}
                <div className="p-3" style={{ backgroundColor: '#f5f6fa', borderBottom: '1px solid #dfe6e9' }}>
                  <div className="row">
                    <div className="col-md-8">
                      <h6 className="mb-1" style={{ color: '#2c3e50' }}>{documentoFirmantes.nombreDocumento}</h6>
                    </div>
                    <div className="col-md-4 text-md-end">
                      <Badge style={{ backgroundColor: documentoFirmantes.ordenFirma === 'paralelo' ? '#3949ab' : '#7f8c8d' }}>
                        {documentoFirmantes.ordenFirma === 'paralelo' ? '⚡ Paralelo' : '📝 Secuencial'}
                      </Badge>
                      <div className="mt-1">{estadoBadge(documentoFirmantes.estado)}</div>
                    </div>
                  </div>
                  
                  {documentoFirmantes.firmantes && (() => {
                    const progreso = calcularProgresoFirmas(documentoFirmantes.firmantes);
                    if (!progreso) return null;
                    return (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="fw-bold text-secondary">Progreso de firmas</small>
                          <small>{progreso.firmados} de {progreso.total} ({progreso.porcentaje}%)</small>
                        </div>
                        <div className="progress" style={{ height: '8px', backgroundColor: '#dfe6e9' }}>
                          <div 
                            className={`progress-bar ${progreso.porcentaje === 100 ? 'bg-success' : ''}`}
                            style={{ width: `${progreso.porcentaje}%`, backgroundColor: progreso.porcentaje === 100 ? undefined : '#3949ab' }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                
                {/* Lista de firmantes */}
                <div className="p-3">
                  {documentoFirmantes.firmantes?.map((firmante, index) => {
                    const color = obtenerColor(index);
                    return (
                      <div 
                        key={index}
                        className="card mb-2 border-0 shadow-sm"
                        style={{ 
                          borderLeft: `5px solid ${color.border} !important`,
                          borderLeftWidth: '5px',
                          borderLeftStyle: 'solid',
                          borderLeftColor: color.border,
                          backgroundColor: firmante.estado === 'firmado' ? '#e8f5e9' : 
                                          firmante.estado === 'en_firma' ? '#e3f2fd' :
                                          firmante.estado === 'rechazado' ? '#ffebee' : 
                                          firmante.estado === 'cancelado' ? '#f5f5f5' : 'white',
                          opacity: firmante.estado === 'cancelado' ? 0.7 : 1
                        }}
                      >
                        <div className="card-body p-3">
                          <div className="row align-items-center">
                            <div className="col-md-4 mb-2 mb-md-0">
                              <div className="d-flex align-items-center">
                                <span 
                                  className="badge rounded-circle d-flex align-items-center justify-content-center me-2"
                                  style={{ width: '30px', height: '30px', backgroundColor: color.border, color: 'white', fontSize: '0.85rem', flexShrink: 0 }}
                                >
                                  {index + 1}
                                </span>
                                <div>
                                  <strong style={{ color: color.text }}>{firmante.nombre}</strong>
                                  <div className="small text-muted">
                                    CC: {firmante.cedula}
                                    <Button variant="link" size="sm" className="p-0 ms-1" onClick={() => copyToClipboard(firmante.cedula)}>
                                      <i className="bi bi-clipboard small"></i>
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="col-md-3 mb-2 mb-md-0">
                              <small className="text-muted d-block"><i className="bi bi-envelope me-1"></i>{firmante.correo || '-'}</small>
                              <small className="text-muted d-block"><i className="bi bi-phone me-1"></i>{firmante.celular || '-'}</small>
                            </div>
                            
                            <div className="col-md-3 mb-2 mb-md-0 text-center">
                              <div className="mb-1">{renderMetodoEnvio(firmante.metodoEnvio)}</div>
                              {estadoFirmanteBadge(firmante.estado || 'pendiente')}
                              {firmante.estadoCerticamaraFirmante && (
                                <div className="mt-1">{estadoCerticamaraBadge(firmante.estadoCerticamaraFirmante)}</div>
                              )}
                            </div>
                            
                            <div className="col-md-2 text-center">
                              {firmante.estado === 'firmado' ? (
                                <span className="text-success" style={{ fontSize: '1.4rem' }}><i className="bi bi-check-circle-fill"></i></span>
                              ) : firmante.estado === 'en_firma' ? (
                                <OverlayTrigger overlay={<Tooltip>Completó BIOFIRMAS, esperando firma digital</Tooltip>}>
                                  <span><Spinner size="sm" variant="primary" /><br/><small className="text-primary" style={{ fontSize: '0.7rem' }}>En firma</small></span>
                                </OverlayTrigger>
                              ) : firmante.estado === 'rechazado' ? (
                                <OverlayTrigger overlay={<Tooltip>Rechazó el documento en Certicámara</Tooltip>}>
                                  <span className="text-danger" style={{ fontSize: '1.4rem' }}><i className="bi bi-x-circle-fill"></i></span>
                                </OverlayTrigger>
                              ) : firmante.estado === 'cancelado' ? (
                                <OverlayTrigger overlay={<Tooltip>{firmante.motivoCancelacion || 'Cancelado por rechazo de otro firmante'}</Tooltip>}>
                                  <span className="text-secondary" style={{ fontSize: '1.4rem' }}><i className="bi bi-slash-circle-fill"></i></span>
                                </OverlayTrigger>
                              ) : (
                                <Button variant="primary" size="sm" onClick={() => handleAbrirReenvio(documentoFirmantes, index)} style={{ backgroundColor: '#3949ab', borderColor: '#3949ab' }}>
                                  <i className="bi bi-arrow-repeat me-1"></i>Reenviar
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {firmante.fechaFirma && (
                            <div className="mt-2 pt-2 border-top">
                              <small className="text-success">
                                <i className="bi bi-calendar-check me-1"></i>
                                Firmado: {new Date(firmante.fechaFirma).toLocaleString('es-CO')}
                              </small>
                            </div>
                          )}
                          {firmante.estado === 'rechazado' && firmante.fechaRechazo && (
                            <div className="mt-2 pt-2 border-top">
                              <small className="text-danger">
                                <i className="bi bi-x-circle me-1"></i>
                                Rechazado: {new Date(firmante.fechaRechazo).toLocaleString('es-CO')}
                              </small>
                            </div>
                          )}
                          {firmante.estado === 'cancelado' && (
                            <div className="mt-2 pt-2 border-top">
                              <small className="text-secondary">
                                <i className="bi bi-slash-circle me-1"></i>
                                {firmante.motivoCancelacion || 'Cancelado por rechazo de otro firmante'}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setMostrarModalFirmantes(false)}>Cerrar</Button>
          </Modal.Footer>
        </Modal>

        {/* Modal visualizar documento */}
        <Modal show={mostrarModal} onHide={handleCerrarModal} centered size="xl" fullscreen="lg-down">
          <Modal.Header closeButton style={{ backgroundColor: '#2c3e50', color: 'white' }}>
            <Modal.Title>📋 Documento</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0" style={{ height: '80vh' }}>
            {documentoSeleccionado && (
              <iframe src={documentoSeleccionado} title="PDF" width="100%" height="100%" style={{ border: 'none' }} />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCerrarModal}>Cerrar</Button>
            {documentoSeleccionado && (
              <a href={documentoSeleccionado} download className="btn btn-success">
                <i className="bi bi-download me-1"></i>Descargar
              </a>
            )}
          </Modal.Footer>
        </Modal>

        {/* Modal reenvío */}
        <Modal show={mostrarModalReenvio} onHide={() => setMostrarModalReenvio(false)} centered>
          <Modal.Header closeButton style={{ backgroundColor: '#2c3e50', color: 'white' }}>
            <Modal.Title>
              <i className="bi bi-arrow-repeat me-2"></i>
              Reenviar {firmanteReenvioIndex !== null ? 'a Firmante' : 'Documento'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {documentoReenvio && (
              <div>
                <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#f5f6fa' }}>
                  <div className="small text-muted">Documento:</div>
                  <div className="fw-bold" style={{ color: '#2c3e50' }}>{documentoReenvio.nombreDocumento}</div>
                  
                  {firmanteReenvioIndex !== null && documentoReenvio.firmantes && (
                    <div className="mt-2 pt-2 border-top" style={{ borderLeft: `4px solid ${obtenerColor(firmanteReenvioIndex).border}`, paddingLeft: '10px' }}>
                      <div className="small text-muted">Firmante #{firmanteReenvioIndex + 1}:</div>
                      <div className="fw-bold" style={{ color: obtenerColor(firmanteReenvioIndex).text }}>
                        {documentoReenvio.firmantes[firmanteReenvioIndex]?.nombre}
                      </div>
                      <div className="small">CC: {documentoReenvio.firmantes[firmanteReenvioIndex]?.cedula}</div>
                    </div>
                  )}
                </div>
                
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-secondary"><i className="bi bi-envelope me-2"></i>Correo</Form.Label>
                    <Form.Control type="email" placeholder="correo@ejemplo.com" value={correoReenvio} onChange={(e) => setCorreoReenvio(e.target.value)} disabled={enviandoReenvio} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-secondary"><i className="bi bi-phone me-2"></i>Celular</Form.Label>
                    <Form.Control type="tel" placeholder="3001234567" value={celularReenvio} onChange={(e) => setCelularReenvio(e.target.value.replace(/\D/g, '').slice(0, 10))} disabled={enviandoReenvio} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-secondary"><i className="bi bi-send me-2"></i>Métodos de envío</Form.Label>
                    <div className="d-flex flex-wrap gap-3">
                      <Form.Check type="checkbox" id="metodo-correo-admin" label={<><i className="bi bi-envelope-fill me-1"></i>Correo</>} checked={metodosReenvio.includes('correo')} onChange={() => handleToggleMetodo('correo')} disabled={enviandoReenvio || !correoReenvio} />
                      <Form.Check type="checkbox" id="metodo-sms-admin" label={<><i className="bi bi-chat-dots-fill me-1"></i>SMS</>} checked={metodosReenvio.includes('sms')} onChange={() => handleToggleMetodo('sms')} disabled={enviandoReenvio || !celularReenvio} />
                    </div>
                  </Form.Group>
                </Form>
                
                {mensajeReenvio && (
                  <Alert variant={mensajeReenvio.includes('✅') ? 'success' : mensajeReenvio.includes('❌') ? 'danger' : 'info'}>
                    {mensajeReenvio}
                  </Alert>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setMostrarModalReenvio(false)} disabled={enviandoReenvio}>Cancelar</Button>
            <Button variant="primary" onClick={handleReenviarDocumento} disabled={enviandoReenvio || (!correoReenvio && !celularReenvio)} style={{ backgroundColor: '#3949ab', borderColor: '#3949ab' }}>
              {enviandoReenvio ? (<><Spinner size="sm" className="me-2" />Reenviando...</>) : (<><i className="bi bi-arrow-repeat me-2"></i>Reenviar</>)}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default AdministrativeDocumentsPage;